/**
 * The languages matrix — Read / Write / Speak per language (registration spec).
 *
 * The form asks, for each language, which of reading, writing and speaking the
 * volunteer can do. That is a small structure, and it is stored in one TEXT
 * column (`language_skills`) rather than its own table or a JSONB blob, so it
 * rides the same copy-from-submission-to-volunteer machinery as every other
 * personal field. This module is the one place that knows the encoding, so the
 * form, the API and the profile all agree on it.
 *
 * Wire/stored form:  'ENGLISH:READ|WRITE|SPEAK;HINDI:SPEAK'
 *   - languages separated by ';'
 *   - a language and its abilities separated by ':'
 *   - abilities separated by '|'
 * A language with no ability ticked is dropped entirely, so an empty matrix is
 * null rather than a string of empty entries.
 */
export const LANGUAGE_SKILL = {
    READ: 'READ',
    WRITE: 'WRITE',
    SPEAK: 'SPEAK',
};
export const LANGUAGE_SKILLS = Object.values(LANGUAGE_SKILL);
const ABILITY_ORDER = { READ: 0, WRITE: 1, SPEAK: 2 };
function isLanguageSkill(value) {
    return value === 'READ' || value === 'WRITE' || value === 'SPEAK';
}
/**
 * Encode a matrix to its stored string, or null if nothing is ticked.
 *
 * Abilities are de-duplicated and ordered Read, Write, Speak so the same choices
 * always produce the same string — otherwise re-saving an untouched form would
 * look like a change to the audit trail.
 */
export function encodeLanguageSkills(map) {
    if (!map)
        return null;
    const entries = [];
    for (const [language, abilities] of Object.entries(map)) {
        const clean = [...new Set(abilities)]
            .filter(isLanguageSkill)
            .sort((a, b) => ABILITY_ORDER[a] - ABILITY_ORDER[b]);
        if (clean.length > 0)
            entries.push(`${language}:${clean.join('|')}`);
    }
    entries.sort();
    return entries.length > 0 ? entries.join(';') : null;
}
/** Decode a stored string back to a matrix. Null or blank yields an empty map. */
export function decodeLanguageSkills(value) {
    const map = {};
    if (!value)
        return map;
    for (const entry of value.split(';')) {
        const [language, abilities] = entry.split(':');
        if (!language || !abilities)
            continue;
        const clean = abilities
            .split('|')
            .map((ability) => ability.trim())
            .filter(isLanguageSkill);
        if (clean.length > 0)
            map[language.trim()] = clean;
    }
    return map;
}
/** The language codes named in a matrix — for validating them against LANGUAGE. */
export function languageCodesIn(map) {
    return Object.keys(map);
}
//# sourceMappingURL=language-skills.js.map